/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Hides a "|" separator that starts or ends a line of a wrapping link row.
 *
 * On a phone the footer's links wrap, and a separator left at the end of a
 * line ("Home | Support | About Deskshare |") or the start of the next reads
 * as a mistake. A separator belongs only between two links on the same line.
 * Which separators those are depends on the font, the language and the screen
 * width, so it is measured: a separator whose neighbour sits on another line,
 * or that has no neighbour, gets EDGE_CLASS (visibility: hidden in the
 * stylesheet, so nothing moves and the measurement does not change itself).
 *
 * The support forum reproduces this footer and does the same.
 */

export const SEPARATOR_CLASS = "footer-sep";
export const EDGE_CLASS = "footer-sep--edge";

/** Vertical centre of an element's box, in pixels. */
function centre(el: Element): number {
  const box = el.getBoundingClientRect();
  return box.top + box.height / 2;
}

/**
 * Marks every separator in `container` that starts or ends a line, and clears
 * the mark from every other one.
 *
 * @param tolerance pixels two centres may differ by and still share a line.
 * @returns how many separators were marked.
 */
export function markLineEdgeSeparators(
  container: ParentNode,
  tolerance = 2,
): number {
  let marked = 0;
  container
    .querySelectorAll<HTMLElement>(`.${SEPARATOR_CLASS}`)
    .forEach((separator) => {
      const before = separator.previousElementSibling;
      const after = separator.nextElementSibling;
      const middle = centre(separator);
      const atEdge =
        !before ||
        !after ||
        Math.abs(centre(before) - middle) > tolerance ||
        Math.abs(centre(after) - middle) > tolerance;
      separator.classList.toggle(EDGE_CLASS, atEdge);
      if (atEdge) marked += 1;
    });
  return marked;
}
